import { Injectable } from '@angular/core';
import { ModalController, AlertController, NavController, LoadingController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { AlertService } from './alert.service';
import { ApiOutcomeService } from '../api-services/scene-outcome.api.service';
import { Outcome } from '../models/outcomes/Outcome';
import { OutcomeInScene } from '../models/outcomes/OutcomeInScene';
import { Scene } from '../models/Scene';
import { SceneOutcomeRelation } from '../models/SceneOutcomeRelation';
import { ApiSceneOutcomeRelationService } from '../api-services/scene-outcome-relation.api.service';
import { ValidationService } from './validation.service';
import { AddOutcomeModalPage } from '../modals/add-outcome-modal/add-outcome-modal.page'
import { AddSubOutcomeToOutcomeModalPage } from '../modals/add-sub-outcome-to-outcome-modal/add-sub-outcome-to-outcome-modal.page';
import { MessagesService } from './messages.service';
import { DeepCopyService } from './deep-copy.service';
import { OutcomeGroupOutcomeRelation } from '../models/outcomes/OutcomeGroupOutcomeRelation';
import { CryptoService } from './crypto.service';
import { StorageService } from './storageService.service';

@Injectable({
  providedIn: 'root'
})
export class OutcomesService {
  public displayOutcomes: boolean = false;
  public outcomesInScene: OutcomeInScene[] = [];  // This contains the outcome contained within the scene.
  public outcomes: Outcome[] = []; // This contains all the outcomes stored throughout the organisation.
  public outcomesCopy: Outcome[] = [];
  public outcomeGroups: Outcome[] = [];
  public outcomeGroupsCopy: Outcome[] = [];
  public outcomeRelations: OutcomeGroupOutcomeRelation[] = [];
  public subOutcomes: Outcome[] = [];
  public allOutcomes: Outcome[] = [];
  private _isModalOpen: boolean = false;

  public outcomeFilter = {
    title: '',
    credits: null,
    expiryDate: null,
    level: '',
  };

  public outcomeGroupFilter = {
    title: '',
    credits: null,
    expiryDate: null,
    level: '',
  };

  constructor(
    public alertController: AlertController,
    public modalController: ModalController,
    public alertService: AlertService,
    public navController: NavController,
    public outcomeApiService: ApiOutcomeService,
    public sceneOutcomeRelationApiService: ApiSceneOutcomeRelationService,
    public validationService: ValidationService,
    private storage: Storage,
    private deepCopyService: DeepCopyService,
    public loadingController: LoadingController,
    public messagesService: MessagesService,
    public cryptoService: CryptoService,
    public storageService: StorageService
  ) {

    this.getOutcomesFromDb();
  }

  getOutcomesFromDb() {
    return new Promise<void>((resolve) => {
      this.outcomeApiService.getOutcomesWithCustomerId(+this.cryptoService.decryptData(this.storageService.getItem('customerId'))).subscribe(outcomes => {
        outcomes.forEach(outcome => {
          outcome.expanded = false;
          outcome.active = true;
        })
        this.allOutcomes = outcomes;
        this.outcomes = outcomes.filter(x => !x.isGroup);
        this.outcomesCopy = outcomes.filter(x => !x.isGroup);
        this.outcomeGroups = outcomes.filter(x => x.isGroup);
        this.outcomeGroupsCopy = outcomes.filter(x => x.isGroup);
        resolve();
      });
    });
  }

  getSubOutcomesOnlyFromDb() {
    return new Promise<void>((resolve) => {
      this.outcomeApiService.getOutcomesWithCustomerId(+this.cryptoService.decryptData(this.storageService.getItem('customerId'))).subscribe(outcomes => {
        outcomes.forEach(outcome => {
          outcome.active = true;
        })
        this.allOutcomes = outcomes;
        this.outcomes = outcomes.filter(x => !x.isGroup);
        this.outcomesCopy = outcomes.filter(x => !x.isGroup);
        resolve();
      });
    });
  }

  reinitOutcomes() {
    this.outcomes = this.outcomesCopy;
  }

  getOutcomesInScene(sceneId) {
    this.outcomesInScene = [];
    this.sceneOutcomeRelationApiService.getSceneOutcomeRelationsWithSceneId(sceneId).subscribe(relations => {
      relations.forEach(relation => {
        var outcome: Outcome = this.outcomes.filter(o => o.outcomeId == relation.outcomeId)[0];
        if (outcome != null) {
          var outcomeInScene: OutcomeInScene = new OutcomeInScene();
          outcomeInScene.outcome = outcome;
          outcomeInScene.sceneOutcomeRelation = relation;
          this.outcomesInScene.push(outcomeInScene)
        }
      })
    })
  }

  setSceneOutcomeAsInScene(outcomeId) {
    this.outcomes.filter(o => o.outcomeId == outcomeId)[0].inScene = true;
  }

  doesSceneOutcomeRelationExist(outcomeId: number): boolean {
    var check = false;
    this.outcomesInScene.forEach(outcomeInScene => {
      if (outcomeInScene.sceneOutcomeRelation.outcomeId == outcomeId) {
        check = true;
      }
    })

    return check;
  }

  saveOutcomes() {
    if (this.outcomes.length > 0) {
      this.storageService.setItem('outcomes', this.cryptoService.encryptData(JSON.stringify(this.outcomes)));
    }
  }

  addOutcomeToScene(outcomeId: number, sceneId: number) {
    var sceneOutcomeRelation: SceneOutcomeRelation = this.createSceneOutcomeRelation(outcomeId, sceneId);
    this.sceneOutcomeRelationApiService.addSceneOutcomeRelation(sceneOutcomeRelation).subscribe(sceneOutcomeRelation => {
      var outcomeInScene: OutcomeInScene = new OutcomeInScene();
      outcomeInScene.outcome = this.outcomes.filter(o => o.outcomeId == outcomeId)[0];
      outcomeInScene.sceneOutcomeRelation = sceneOutcomeRelation;
      this.outcomesInScene.push(outcomeInScene);
    });
  }

  removeOutcomeFromScene(outcomeId: number, sceneId: number) {

    this.sceneOutcomeRelationApiService.getSceneOutcomeRelationsWithSceneId(sceneId).subscribe(relations => {

      var sceneOutcomeRelation: SceneOutcomeRelation = relations.filter(s => s.outcomeId == outcomeId)[0];
      this.sceneOutcomeRelationApiService.deleteSceneOutcomeRelation(sceneOutcomeRelation.sceneOutcomeRelationId).subscribe(sceneOutcomeRelationFromDb => {
        var index = 0;
        this.outcomesInScene.forEach(outcomeInScene => {
          if (outcomeInScene.sceneOutcomeRelation.sceneOutcomeRelationId == sceneOutcomeRelationFromDb.sceneOutcomeRelationId) {
            this.outcomesInScene.splice(index, 1);
          }
          index++;
        })
      });
    });
  }

  createSceneOutcomeRelation(outcomeId: number, sceneId: number): SceneOutcomeRelation {
    return {
      sceneId: sceneId,
      outcomeId: outcomeId,
      sceneOutcomeRelationId: 0
    }
  }

  async deleteOutcome(outcome: Outcome) {
    const alert = await this.alertController.create({
      header: 'Please confirm',
      message: "are you sure you want to delete the outcome '" + outcome.title + "'?",
      buttons: [{
        text: 'Cancel',
      }, {
        text: 'Delete',
        handler: () => {
          outcome.active = false;
          this.messagesService.presentLoadingWindow("Deleting Outcome...");
          this.outcomeApiService.deleteOutcome(outcome.outcomeId).subscribe(x => {
            this.messagesService.loadingWindow.dismiss();
            if (outcome.isGroup) {
              var outcomeGroupToBeDeleted = this.outcomeGroups.indexOf(outcome);
              this.outcomeGroups.splice(outcomeGroupToBeDeleted, 1);
              this.outcomeGroupsCopy.splice(outcomeGroupToBeDeleted, 1);
            }
            else {
              var outcomeIndexToBeDeleted = this.outcomes.indexOf(outcome);
              this.outcomes.splice(outcomeIndexToBeDeleted, 1);
              this.outcomesCopy.splice(outcomeIndexToBeDeleted, 1);
            }
            this.getOutcomesFromDb();
            this.messagesService.presentToast('Outcome deleted successfully!', 'success', 'Outcomes');
          });
        }
      }
      ]
    });
    await alert.present();
  }

  openAddOutcomeModal(outcome, isGroup) {
    if (outcome != null) {
      return this.editOutcome(outcome, isGroup);
    }
    else {
      return this.addOutcome(isGroup);
    }
  }

  async SetOutcomeRelations(outcomeGroupId) {
    return new Promise<void>((resolve) => {
      this.getOutcomeForGroup(outcomeGroupId).subscribe(outcomesFound => {
        this.outcomeRelations = outcomesFound;
        resolve();
      })
    });
  }

  async getSubOutcomes(outcomeGroupId) {
    return new Promise<void>((resolve) => {
      this.getOutcomeForGroup(outcomeGroupId).subscribe(outcomesAdded => {
        this.getSubOutcomesOnlyFromDb().then(x => {
          this.subOutcomes = this.outcomes.filter(x => outcomesAdded.find(y => y.outcomeId == x.outcomeId));
          resolve();
        }
        );
      });

    });
  }

  async openAddOutcomeToGroupModal(outcomeGroupId) {
    await this.SetOutcomeRelations(outcomeGroupId);

    var outcomesForGroup = this.deepCopyService.deepCopy(this.outcomes);
    if (this.outcomeRelations.length != 0 && outcomesForGroup.length != 0) {
      outcomesForGroup.forEach(outcome => {
        var outcomeFound = this.outcomeRelations.filter(x => x.outcomeId == outcome.outcomeId)[0];

        if (outcomeFound) {
          outcome.checked = true;
        }
      });
    }

    const addModal = await this.modalController.create({
      component: AddSubOutcomeToOutcomeModalPage,
      id: 'add-sub-outcome-modal',
      componentProps: {
        "outcomeGroupId": outcomeGroupId,
        "subOutcomes": outcomesForGroup,
        "subOutcomeCount": outcomesForGroup.length
      },
    });
    await addModal.present();
    return addModal;
  }

  async addOutcome(isGroup: boolean) {
    
    if(this._isModalOpen === true) return;
    this._isModalOpen = true;

    const addModal = await this.modalController.create({
      component: AddOutcomeModalPage,
      id: 'add-outcome-modal',
      componentProps: {
        "isGroup": isGroup,
      },
    });


    addModal.onDidDismiss().then(result => {
      if (result.data != null) {
        if (this.validationService.isOutcomeValid(result.data)) {
          this.outcomeApiService.addOutcome(result.data).subscribe(res => {
            this.messagesService.presentLoadingWindow("Adding learning objective...").then(x => {
              this.getOutcomesFromDb().then(x => {
                this.messagesService.loadingWindow.dismiss();
              });
              this.messagesService.presentToast('New learning objective added successfully!', 'success', 'Learning Objectives');

            });
          });
        }
        else {
          this.validationService.alertForMissingOutcomeProperties(result.data);
        }
      }
      this._isModalOpen = false;
    });
    return await addModal.present();
  }

  editOutcome(outcome, isGroup): Promise<void> {

    return new Promise<void>(async (resolve) => {
      if(this._isModalOpen === true) return;
      this._isModalOpen = true;

      var outcomeCopy = this.deepCopyService.deepCopy(outcome);
      const editModal = await this.modalController.create({
        component: AddOutcomeModalPage,
        id: 'add-outcome-modal',
        componentProps: {
          "outcome": outcomeCopy,
          "isEditing": true,
          "isGroup": isGroup
        }
      });

      editModal.onDidDismiss().then(result => {
        if (result.data != null) {
            this.messagesService.presentLoadingWindow("Saving learning objective...", "saving-outcome");
            this.outcomeApiService.updateOutcome(result.data.outcomeId, result.data).subscribe(res => {
              this.getOutcomesFromDb().then(x => {
                this.messagesService.dismissLoadingWindowById("saving-outcome");
                resolve();
              });
              this.messagesService.presentToast('Learning objective saved successfully!', 'success', 'Learning Objectives');
            });
        } else {
          this.resetOutcome(outcome);
          resolve();
        }
        this._isModalOpen = false;
      });

      await editModal.present();
    });
  }

  resetOutcome(outcome: Outcome) {
    this.outcomeApiService.getOutcome(outcome.outcomeId).subscribe(outcome => {
      var o: Outcome = this.outcomes.filter(a => a.outcomeId == outcome.outcomeId)[0];
      if (o) {
        o.title = outcome.title;
        o.level = outcome.level;
        o.credits = outcome.credits;
        o.learningOutcomes = outcome.learningOutcomes;
        o.assessmentCriteria = outcome.assessmentCriteria;
        o.unitPurpose = outcome.unitPurpose;
        o.expiryDate = outcome.expiryDate;
      }
    })
  }

  async alertForIncorrectData(message: string) {
    const alert = await this.alertController.create({
      header: 'Learning Objective Error: ' + message,
      buttons: [{
        text: 'Ok',
      }
      ]
    });
    await alert.present();
  }

  isEmptyOrNull(value: string): boolean {
    return this.validationService.isEmptyOrNull(value);
  }

  ChangeSelectedTab(selectedTab) {
    if (selectedTab == "outcome") {
      this.displayOutcomes = true;
    }
    else {
      this.displayOutcomes = false;
    }
  }

  getOutcomeForGroup(outcomeGroupId) {
    return this.outcomeApiService.getSubOutcomesByOutcomeGroupId(outcomeGroupId);
  }
}
